/**
 * Production Tracker Hooks
 *
 * Data fetching hooks for the tracker page:
 * - useTrackerCounts: combined batch + order counts (5min auto-refresh)
 * - useTrackerBatches: batch list per tab (5min auto-refresh)
 * - useTrackerOrders: order list per tab (5min auto-refresh)
 * - useCommittedOrders / useCommittedSummary: the Committed tab's order list +
 *   its order/unit totals (BLU-811; 5min auto-refresh)
 * - useBatchOrders: lazy-loaded order+garment data for expanded batch rows
 * - useOrderGarments: lazy-loaded garments for expanded order rows
 * - useArchivedBatches: archived batch list (no auto-refresh)
 * - useArchiveBatch / useRestoreBatch: mutation hooks
 */

import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import type { CommittedProductionStatus } from '../lib/order-status-display';

// ============================================================================
// TYPES
// ============================================================================

export type BatchTab = 'in-progress' | 'complete' | 'archived';
/**
 * 'committed' (BLU-811) is a DIFFERENT population from the other two: it is
 * keyed off stock still held at this location for open orders, not off batches,
 * so it has its own RPC and its own summary rather than another `tab` argument
 * on `listTrackerOrders`.
 */
export type OrderTab = 'active' | 'shipped' | 'committed';
/** Order Queue tab shown in Tracker — a subset of production-orders' QueueTab
 * (the 'oos' tab was deprecated in Phase 12.6, so it's never offered here). */
export type QueueViewTab = 'ready' | 'all';
export type TrackerView = 'batches' | 'orders' | 'queue';

type BatchStatus = 'OPEN' | 'PICKING' | 'PICKED' | 'CATCHING' | 'PACKING' | 'COMPLETED' | 'CANCELLED';

/** Maps batch tabs to batch statuses for listBatches queries */
export const BATCH_TAB_STATUSES: Record<Exclude<BatchTab, 'archived'>, BatchStatus[]> = {
  'in-progress': ['OPEN', 'PICKING', 'PICKED', 'CATCHING', 'PACKING'],
  complete: ['COMPLETED'],
};

const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Fetch combined batch + order counts for tracker tab badges.
 * Auto-refreshes every 5 minutes. Manual refresh via refetch().
 */
export function useTrackerCounts(locationId: string | undefined) {
  return trpc.productionOrders.getTrackerCounts.useQuery(
    { locationId: locationId! },
    {
      enabled: !!locationId,
      refetchInterval: REFRESH_INTERVAL,
      staleTime: 5_000,
    }
  );
}

/**
 * Fetch batches for the active tracker tab.
 * Auto-refreshes every 5 minutes. Manual refresh via refetch().
 */
export function useTrackerBatches(options: {
  locationId: string | undefined;
  tab: BatchTab;
  sortOrder: 'newest' | 'oldest';
  page: number;
  pageSize?: number;
  search?: string;
}) {
  const { locationId, tab, sortOrder, page, pageSize = 50, search } = options;
  const offset = page * pageSize;

  const query = trpc.productionOrders.listBatches.useQuery(
    {
      locationId: locationId!,
      statuses: BATCH_TAB_STATUSES[tab as keyof typeof BATCH_TAB_STATUSES] ?? [],
      sortOrder,
      limit: pageSize,
      offset,
      search: search || undefined,
    },
    {
      enabled: !!locationId && tab !== 'archived',
      refetchInterval: REFRESH_INTERVAL,
      staleTime: 5_000,
    }
  );

  const total = query.data?.pagination?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  return {
    batches: query.data?.batches ?? [],
    pagination: query.data?.pagination,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    totalPages,
  };
}

/**
 * Fetch orders for the tracker "Orders" view.
 * Auto-refreshes every 5 minutes.
 *
 * - 'active' tab: excludes terminal orders
 * - 'shipped' tab: only terminal orders
 * - When searching: includes all orders across tabs
 */
export function useTrackerOrders(options: {
  locationId: string | undefined;
  tab: OrderTab;
  sortOrder: 'newest' | 'oldest';
  page: number;
  pageSize?: number;
  search?: string;
  enabled?: boolean;
  inBinOnly?: boolean;
  /** BLU-367: narrow to one client org, server-side. */
  clientOrganizationId?: string;
}) {
  const { locationId, tab, sortOrder, page, pageSize = 50, search, enabled = true, inBinOnly, clientOrganizationId } = options;
  const offset = page * pageSize;
  // Active tab: exclude terminal orders
  // Shipped tab: only terminal orders
  // Search stays within the current tab
  const includeTerminalOrders = tab === 'shipped';
  const terminalOnly = tab === 'shipped';
  // Default Active tab to in-progress batch statuses; clear the filter when
  // searching so an Active-tab search can still find recently-completed orders
  // before they roll into Shipped. Backend now uses the list_tracker_orders
  // RPC (see migration 20260504100000_blu_393_tracker_rpc_no_url_overflow.sql)
  // so the join happens server-side — there is no longer any URL-overflow risk
  // from a wider batch list.
  const batchStatuses = tab === 'active' && !search
    ? (BATCH_TAB_STATUSES['in-progress'] as string[])
    : undefined;

  const query = trpc.productionOrders.listTrackerOrders.useQuery(
    {
      locationId: locationId!,
      batchStatuses,
      includeTerminalOrders,
      terminalOnly,
      sortOrder,
      limit: pageSize,
      offset,
      search: search || undefined,
      inBinOnly: inBinOnly || undefined,
      clientOrganizationId: clientOrganizationId || undefined,
    },
    {
      enabled: !!locationId && enabled,
      refetchInterval: REFRESH_INTERVAL,
      staleTime: 5_000,
    }
  );

  const total = query.data?.pagination?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  return {
    orders: query.data?.orders ?? [],
    pagination: query.data?.pagination,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    totalPages,
  };
}

/**
 * Fetch orders holding committed stock at this location — the Tracker
 * "Committed" tab (BLU-811). Membership is `order_item_locations.quantity > 0`
 * on an OPEN order, evaluated server-side, so search and the client filter are
 * query inputs and the pagination total is the filtered total.
 *
 * Same cadence as its siblings (5-minute refetch, 5s staleTime): the figure it
 * explains is the same figure Inventory shows, so the two must not visibly
 * disagree for longer than one poll.
 */
export function useCommittedOrders(options: {
  locationId: string | undefined;
  sortOrder: 'newest' | 'oldest';
  page: number;
  pageSize?: number;
  search?: string;
  enabled?: boolean;
  /** BLU-367: narrow to one client org, server-side. */
  clientOrganizationId?: string;
  /** BLU-811: narrow to one production status (or the NOT_IN_PRODUCTION bucket),
   *  server-side, so the list/count/pager all reflect the filtered set. */
  productionStatus?: CommittedProductionStatus;
}) {
  const { locationId, sortOrder, page, pageSize = 50, search, enabled = true, clientOrganizationId, productionStatus } = options;
  const offset = page * pageSize;

  const query = trpc.productionOrders.listCommittedOrders.useQuery(
    {
      locationId: locationId!,
      sortOrder,
      limit: pageSize,
      offset,
      search: search || undefined,
      clientOrganizationId: clientOrganizationId || undefined,
      productionStatus: productionStatus || undefined,
    },
    {
      enabled: !!locationId && enabled,
      refetchInterval: REFRESH_INTERVAL,
      staleTime: 5_000,
    }
  );

  const total = query.data?.pagination?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  return {
    orders: query.data?.orders ?? [],
    pagination: query.data?.pagination,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    totalPages,
  };
}

/**
 * Order/unit totals behind the Committed tab, plus the `sku_inventory` figure
 * they should agree with.
 *
 * Enabled for the whole Orders view, not just the Committed tab: it feeds the
 * tab BADGE as well as the summary line, and a badge that only appears once you
 * are already on the tab tells you nothing. Unlike its sibling badges this one
 * is client-filtered, because it shares its source with the line underneath the
 * table and two different numbers for "committed" on one screen is worse than
 * one badge that follows the filter.
 *
 * BLU-811 status filter: `productionStatus` narrows `orderCount` / `unitCount` /
 * `distinctSkus` to one status server-side (the `statusBreakdown` facet always ignores
 * it). The tab BADGE must stay the FLOOR total, so the badge's summary call leaves
 * `productionStatus` undefined and a SEPARATE, status-filtered call feeds the line —
 * see `ProductionTrackerPage`. `includeBreakdown` gates the extra `prod_orders` scan
 * that produces `statusBreakdown`: left off, the every-5-min whole-Orders-view poll
 * stays on the cheap OIL-only path; the page sets it true only while the Committed tab
 * is showing.
 */
export function useCommittedSummary(options: {
  locationId: string | undefined;
  enabled?: boolean;
  clientOrganizationId?: string;
  productionStatus?: CommittedProductionStatus;
  includeBreakdown?: boolean;
}) {
  const { locationId, enabled = true, clientOrganizationId, productionStatus, includeBreakdown } = options;

  return trpc.productionOrders.getCommittedOrdersSummary.useQuery(
    {
      locationId: locationId!,
      clientOrganizationId: clientOrganizationId || undefined,
      productionStatus: productionStatus || undefined,
      // Omit when falsy so the off-tab query key stays byte-identical to before —
      // no new cache entry, no behaviour change for the Active/Shipped polls.
      includeBreakdown: includeBreakdown || undefined,
    },
    {
      enabled: !!locationId && enabled,
      refetchInterval: REFRESH_INTERVAL,
      staleTime: 5_000,
    }
  );
}

/**
 * Fetch orders for the tracker "Order Queue" view — the pre-batch queue
 * (getOrderQueue), NOT the same data as useTrackerOrders' listTrackerOrders
 * (which is post-batch monitoring). Auto-refreshes every 5 minutes.
 */
export function useTrackerQueueOrders(options: {
  locationId: string | undefined;
  tab: QueueViewTab;
  sortOrder: 'newest' | 'oldest';
  page: number;
  pageSize?: number;
  search?: string;
  enabled?: boolean;
  /** BLU-367: narrow to one client org, server-side. */
  clientOrganizationId?: string;
}) {
  const { locationId, tab, sortOrder, page, pageSize = 50, search, enabled = true, clientOrganizationId } = options;
  const offset = page * pageSize;

  // Filters are only sent when at least one is set — keeps the query key (and
  // therefore the cache entry) identical to before for the unfiltered case.
  const filters =
    search || clientOrganizationId
      ? { ...(search ? { search } : {}), ...(clientOrganizationId ? { clientOrganizationId } : {}) }
      : undefined;

  const query = trpc.productionOrders.getOrderQueue.useQuery(
    {
      locationId: locationId!,
      tab,
      sortOrder,
      limit: pageSize,
      offset,
      filters,
    },
    {
      enabled: !!locationId && enabled,
      refetchInterval: REFRESH_INTERVAL,
      staleTime: 5_000,
    }
  );

  const total = query.data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  return {
    orders: query.data?.orders ?? [],
    pagination: { total, offset, hasMore: query.data?.hasMore ?? false },
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    totalPages,
  };
}

/**
 * Fetch Order Queue tab counts (ready/all) for tracker tab badges.
 * Auto-refreshes every 5 minutes.
 */
export function useTrackerQueueCounts(locationId: string | undefined) {
  return trpc.productionOrders.getQueueCounts.useQuery(
    { locationId: locationId! },
    {
      enabled: !!locationId,
      refetchInterval: REFRESH_INTERVAL,
      staleTime: 5_000,
    }
  );
}

/**
 * Lazy-load orders and garments for an expanded batch row.
 *
 * Uses manual fetch trigger — not auto-refreshed.
 * Data is cached after first load; user re-expands to refresh.
 */
export function useBatchOrders(batchId: string | null) {
  return trpc.productionOrders.getBatchOrdersWithGarments.useQuery(
    { batchId: batchId! },
    {
      enabled: !!batchId,
      staleTime: 30_000, // Cache for 30s
    }
  );
}

/**
 * Lazy-load garments for an expanded order row (Orders view).
 * Cached for 30s, not auto-refreshed.
 *
 * Returns `{ garments, siblings, hasOpenPrintFileIssue }` — BLU-758 added
 * `siblings`, the order's OTHER live fulfilment-order parts (see
 * `ExpandedOrderContent`'s `TrackerOrderSibling`, inferred straight off the
 * router so the shape can't drift). Sibling fields are REDACTED to null for
 * floors the caller has no relationship with; render around them rather than
 * showing "null". BLU-817 added `hasOpenPrintFileIssue` (order-level rollup)
 * and per-garment `hasOpenPrintFileIssue` / `printFileIssueReason`.
 */
export function useOrderGarments(orderId: string | null) {
  return trpc.productionOrders.getOrderGarments.useQuery(
    { orderId: orderId! },
    {
      enabled: !!orderId,
      staleTime: 30_000,
    }
  );
}

// ============================================================================
// ARCHIVED BATCHES
// ============================================================================

/**
 * Fetch archived batches for the Archived tab.
 * No auto-refresh (archived data is static).
 */
export function useArchivedBatches(options: {
  locationId: string | undefined;
  sortOrder: 'newest' | 'oldest';
  page: number;
  pageSize?: number;
  search?: string;
  enabled?: boolean;
}) {
  const { locationId, sortOrder, page, pageSize = 50, search, enabled = true } = options;
  const offset = page * pageSize;

  const query = trpc.productionOrders.listArchivedBatches.useQuery(
    {
      locationId: locationId!,
      sortOrder,
      limit: pageSize,
      offset,
      search: search || undefined,
    },
    {
      enabled: !!locationId && enabled,
      staleTime: 30_000,
    }
  );

  const total = query.data?.pagination?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  return {
    batches: query.data?.batches ?? [],
    pagination: query.data?.pagination,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    totalPages,
  };
}

// ============================================================================
// ARCHIVE MUTATIONS
// ============================================================================

/**
 * Committed-tab membership is derived (open order × stock still held here), so
 * it can move under any mutation that touches an order's lifecycle — and both
 * the list AND the summary have to go, or the badge and the line keep quoting
 * the pre-mutation figure while the table shows the new one.
 */
function invalidateCommittedQueries(utils: ReturnType<typeof trpc.useUtils>) {
  utils.productionOrders.listCommittedOrders.invalidate();
  utils.productionOrders.getCommittedOrdersSummary.invalidate();
}

/**
 * Archive a batch. Invalidates batch lists and counts on success.
 */
export function useArchiveBatch() {
  const utils = trpc.useUtils();

  return trpc.productionOrders.archiveBatch.useMutation({
    onSuccess: () => {
      utils.productionOrders.listBatches.invalidate();
      utils.filmPrint.listDtfBatches.invalidate();
      utils.productionOrders.getTrackerCounts.invalidate();
      utils.productionOrders.listTrackerOrders.invalidate();
      utils.productionOrders.listArchivedBatches.invalidate();
      invalidateCommittedQueries(utils);
    },
  });
}

/**
 * Restore an archived batch. Invalidates batch lists and counts on success.
 */
export function useRestoreBatch() {
  const utils = trpc.useUtils();

  return trpc.productionOrders.restoreBatch.useMutation({
    onSuccess: () => {
      utils.productionOrders.listBatches.invalidate();
      utils.filmPrint.listDtfBatches.invalidate();
      utils.productionOrders.getTrackerCounts.invalidate();
      utils.productionOrders.listTrackerOrders.invalidate();
      utils.productionOrders.listArchivedBatches.invalidate();
      invalidateCommittedQueries(utils);
    },
  });
}

// ============================================================================
// MARK SHIPPED MUTATION
// ============================================================================

/**
 * Mark an order as manually shipped from the tracker.
 * Wraps shipping.markManualComplete with tracker-specific query invalidation.
 */
export function useMarkOrderShipped() {
  const utils = trpc.useUtils();

  return trpc.shipping.markManualComplete.useMutation({
    onSuccess: () => {
      utils.productionOrders.getBatchOrdersWithGarments.invalidate();
      utils.productionOrders.listBatches.invalidate();
      utils.filmPrint.listDtfBatches.invalidate();
      utils.productionOrders.getTrackerCounts.invalidate();
      utils.productionOrders.listTrackerOrders.invalidate();
      // Shipping is the main way a row LEAVES the Committed tab — without this
      // the order sits there claiming to hold stock it has already released.
      invalidateCommittedQueries(utils);
    },
  });
}

// ============================================================================
// STATE MANAGEMENT
// ============================================================================

/**
 * Manages tracker page state with separate tab states per view.
 *
 * Orders view: 'active' | 'shipped' | 'committed'
 * Batches view: 'in-progress' | 'complete' | 'archived'
 */
export function useTrackerState() {
  const [batchTab, setBatchTabInternal] = useState<BatchTab>('in-progress');
  const [orderTab, setOrderTabInternal] = useState<OrderTab>('active');
  const [queueTab, setQueueTabInternal] = useState<QueueViewTab>('ready');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('oldest');
  const [currentPage, setCurrentPage] = useState(0);
  const [view, setViewInternal] = useState<TrackerView>('orders');
  const [inBinOnly, setInBinOnly] = useState(false);

  const handleBatchTabChange = (tab: BatchTab) => {
    setBatchTabInternal(tab);
    setCurrentPage(0);
  };

  const handleOrderTabChange = (tab: OrderTab) => {
    setOrderTabInternal(tab);
    setCurrentPage(0);
    setInBinOnly(false);
  };

  const handleQueueTabChange = (tab: QueueViewTab) => {
    setQueueTabInternal(tab);
    setCurrentPage(0);
  };

  const handleViewChange = (v: TrackerView) => {
    setViewInternal(v);
    setCurrentPage(0);
    setInBinOnly(false);
  };

  return {
    batchTab,
    setBatchTab: handleBatchTabChange,
    orderTab,
    setOrderTab: handleOrderTabChange,
    queueTab,
    setQueueTab: handleQueueTabChange,
    sortOrder,
    setSortOrder,
    currentPage,
    setCurrentPage,
    view,
    setView: handleViewChange,
    inBinOnly,
    setInBinOnly,
  };
}
